import React from "react";
import { motion } from "framer-motion";
import { FileText, Info } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { impressumContent } from "@/i18n/impressum";

// Simple linkifier for URLs in body text
function renderBody(text) {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((p, i) => {
        if (/^https?:\/\//.test(p)) {
            return (
                <a
                    key={i}
                    href={p}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-caireen-orange hover:text-caireen-red underline decoration-caireen-orange/30 underline-offset-2"
                >
                    {p}
                </a>
            );
        }
        return <React.Fragment key={i}>{p}</React.Fragment>;
    });
}

export default function Impressum() {
    const { lang } = useLang();
    const c = impressumContent[lang] || impressumContent.de;

    return (
        <div data-testid="page-impressum" className="px-6 md:px-10 py-12 md:py-20">
            <div className="max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-caireen-ink/5 text-caireen-ink/70 font-sans text-xs tracking-widest uppercase">
                        <FileText className="w-3.5 h-3.5" />
                        {c.title}
                    </span>
                    <h1 className="mt-5 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
                        {c.title}
                    </h1>
                    <p className="mt-5 font-sans text-base md:text-lg text-caireen-ink/70 max-w-3xl">
                        {c.subtitle}
                    </p>

                    <div className="mt-6 inline-flex items-start gap-2 rounded-2xl bg-caireen-yellow/15 px-4 py-3 border border-caireen-yellow/40 font-sans text-sm text-caireen-ink/80 max-w-3xl">
                        <Info className="w-4 h-4 text-caireen-orange shrink-0 mt-0.5" />
                        <span>{c.note}</span>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="mt-12 bg-white rounded-3xl ring-1 ring-caireen-ink/10 divide-y divide-caireen-ink/10 overflow-hidden"
                >
                    {c.sections.map((s, i) => (
                        <section
                            key={i}
                            data-testid={`impressum-section-${i}`}
                            className="px-6 md:px-10 py-7 md:py-8"
                        >
                            <h2 className="font-serif text-xl md:text-2xl tracking-tight text-caireen-ink">
                                {s.heading}
                            </h2>
                            <p className="mt-3 font-sans text-[15px] leading-relaxed text-caireen-ink/75 whitespace-pre-line">
                                {renderBody(s.body)}
                            </p>
                        </section>
                    ))}
                </motion.div>

                <p className="mt-8 font-sans text-xs text-caireen-ink/45 text-center">
                    Caireen Verein · ZVR 1121813363 · Innerteuchen 26, 9543 Arriach, Österreich
                </p>
            </div>
        </div>
    );
}
